import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type DayPassDto } from '@gc/shared';

import { isPassLive } from './dayPasses';

/**
 * Passes sold on today's business day (SPEC §6.1, §5.7).
 *
 * `sessionCount` is on every row because it is the question staff actually ask
 * about a pass — "has this been used?" — and because a pass with sittings on it
 * is the one that must not be argued about at closing time.
 */
export function PassList({
  passes,
  nowMs,
  currencySymbol,
  locale,
}: {
  passes: readonly DayPassDto[];
  nowMs: number;
  currencySymbol: string;
  locale?: string;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol, locale });
  const clock = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <h2 className="mb-3 text-base font-semibold text-content">{t('dayPasses.passes.title')}</h2>

      {passes.length === 0 ? (
        <p className="text-sm text-content-muted">{t('dayPasses.passes.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-start text-xs uppercase tracking-wide text-content-muted">
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.holder')}</th>
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.offer')}</th>
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.price')}</th>
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.until')}</th>
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.sessions')}</th>
                <th className="py-1 text-start font-medium">{t('dayPasses.passes.soldBy')}</th>
              </tr>
            </thead>
            <tbody>
              {passes.map((pass) => {
                const live = isPassLive(pass, nowMs);
                return (
                  <tr
                    key={pass.id}
                    data-pass-row={pass.id}
                    className="border-t border-subtle text-content"
                  >
                    <td className="py-2">
                      <span className="block truncate">{pass.holderName}</span>
                      <span
                        className={`text-[11px] ${live ? 'text-primary' : 'text-content-muted'}`}
                      >
                        {live ? t('dayPasses.passes.valid') : t('dayPasses.passes.expired')}
                      </span>
                    </td>
                    <td className="py-2 text-content-secondary">{pass.offerName}</td>
                    <td className="tabular py-2">{money(pass.priceMd)}</td>
                    <td className="tabular py-2">{clock(pass.validUntil)}</td>
                    <td className="tabular py-2">{pass.sessionCount}</td>
                    <td className="py-2 text-content-secondary">{pass.soldByName}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className="mt-3 text-xs text-content-muted">{t('dayPasses.passes.seatHint')}</p>
        </div>
      )}
    </section>
  );
}
